import Styles from "../RFQ/RFQ.module.css";
import React, { useEffect, useState, useCallback } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "../../../commons/Accordion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  getequisitionsList,
  getRaArchive,
  getDocumentTenderRes,
} from "../../../Services/ReverseAuction/GetApi";
import { generatorCreatorArchiveGet } from "../../../Redux/Slices/tokenAccessArchiveSlice";
import { activeTenderId } from "../../../Redux/Slices/auctionSlice";
import { formatDate, formatSlotTime } from "../../../helpers/dateFormat";
import { usePdfPreview } from "../../../hooks/usePdfPreview";
import RFPBidHistory from "../../RFP_Common/RFPBidHistory/RFPBidHistory";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallMadeOutlinedIcon from "@mui/icons-material/CallMadeOutlined";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { fetchMainScreenActiveConnection } from "../../../Redux/Slices/CapacitySlice";
import { getMainScreenConnectionDetails } from "../../../Services/BuySell/Listing";
import LoadMorePagination from "../../LoadeMore";

const EXPANDED_TENDER_KEY = "tcInitiateArchiveExpandedTenderId";

const systemTnCUrl =
  "https://otc2-dev.s3.ap-south-1.amazonaws.com/assets/GNA+Energy+-+System+T%26C+-+RFP_RFQ.pdf";

const LoaderNoData = ({ loading, dataLength }) => {
  if (loading) {
    return (
      <div className={Styles.loaderContainer}>
        <div className={Styles.loader} role="status" aria-label="loading" />
      </div>
    );
  }
  if (!loading && dataLength === 0) {
    return <div className={Styles.noDataContainer}>No records found</div>;
  }
  return null;
};

const SummaryHeader = ({ status }) => {
  const isExpired = status?.toLowerCase() === "expired";
  return (
    <div
      className={Styles.accordionSummaryContainer}
      style={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        backgroundColor: "#fffaf6",
      }}
    >
      <table
        className={Styles.summaryTable}
        style={{ tableLayout: "fixed", width: "100%" }}
      >
        <colgroup>
          <col style={{ width: "5%" }} />
          <col style={{ width: isExpired ? "30%" : "35%" }} />
          <col style={{ width: isExpired ? "35%" : "60%" }} />
          {isExpired && <col style={{ width: "30%" }} />}
        </colgroup>
        <thead className={Styles.summaryHeader}>
          <tr>
            <th className={Styles.summaryHeaderCell}>S.No</th>
            <th className={Styles.summaryHeaderCell}>E-Tender Number</th>
            <th className={Styles.summaryHeaderCell}>Tender Duration</th>
            {isExpired && (
              <th className={Styles.summaryHeaderCell}>Expiry Date & Time</th>
            )}
          </tr>
        </thead>
      </table>
    </div>
  );
};

// Per-status inner column model, mirroring RFQArchive:
// - "full" (Closed / Expired): Contract ID · Date · Time · Req.Qty · Min.Qty ·
//   Lowest Bid (MW / ₹/kWh) · Bid History
// - "withdrawn": Date · Time · Req.Qty · Min.Qty (no Contract ID, no Lowest Bid,
//   no Bid History)
// - "reduced" (any other / open): Date · Time · Req.Qty · Min.Qty · Bid History
const getInnerLayout = (status) => {
  const s = status?.toLowerCase();
  if (s === "withdrawn") return "withdrawn";
  if (s === "closed" || s === "expired") return "full";
  return "reduced";
};

const InnerColGroup = ({ layout }) => {
  if (layout === "full") {
    return (
      <colgroup>
        <col style={{ width: "12%" }} />
        <col style={{ width: "20%" }} />
        <col style={{ width: "16%" }} />
        <col style={{ width: "11%" }} />
        <col style={{ width: "11%" }} />
        <col style={{ width: "18%" }} />
        <col style={{ width: "12%" }} />
      </colgroup>
    );
  }
  if (layout === "withdrawn") {
    return (
      <colgroup>
        <col style={{ width: "34%" }} />
        <col style={{ width: "26%" }} />
        <col style={{ width: "20%" }} />
        <col style={{ width: "20%" }} />
      </colgroup>
    );
  }
  return (
    <colgroup>
      <col style={{ width: "28%" }} />
      <col style={{ width: "22%" }} />
      <col style={{ width: "18%" }} />
      <col style={{ width: "18%" }} />
      <col style={{ width: "14%" }} />
    </colgroup>
  );
};

const SummaryInnerHeaderRow = ({ layout }) => (
  <div
    style={{ marginBottom: "10px" }}
    className={Styles.accordionSummaryContainer}
  >
    <table
      className={Styles.summaryTable}
      style={{ tableLayout: "fixed", width: "100%" }}
    >
      <InnerColGroup layout={layout} />
      <thead className={Styles.summaryHeader}>
        <tr>
          {layout === "full" && (
            <th className={Styles.summaryInnerHeaderCellWrap}>Contract ID</th>
          )}
          <th className={Styles.summaryInnerHeaderCellWrap}>
            Start & End Date
          </th>
          <th className={Styles.summaryInnerHeaderCellWrap}>
            Start & End Time
          </th>
          <th className={Styles.summaryInnerHeaderCellWrap}>Req. Qty (MW)</th>
          <th className={Styles.summaryInnerHeaderCellWrap}>Min. Qty (MW)</th>
          {layout === "full" && (
            <th className={Styles.summaryInnerHeaderCellWrap}>
              Lowest Bid (MW / ₹/kWh)
            </th>
          )}
          {layout !== "withdrawn" && (
            <th className={Styles.summaryInnerHeaderCellWrap}>Bid History</th>
          )}
        </tr>
      </thead>
    </table>
  </div>
);

function formatCustomDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  let hours = String(date.getHours()).padStart(2, "0");
  let minutes = String(date.getMinutes()).padStart(2, "0");
  if (hours === "23" && minutes === "59") {
    hours = "24";
    minutes = "00";
  }
  return `${day} ${month} ${year} ${hours}:${minutes}`;
}

function formatExpireDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const time =
    hours === "23" && minutes === "59" ? "24:00" : `${hours}:${minutes}`;
  return `${day}-${month}-${year} ${time}`;
}

const ReqRowCells = ({
  item,
  layout,
  contractId,
  bidCell,
  onViewBidHistory,
}) => (
  <>
    {layout === "full" && (
      <td className={Styles.summaryCellWrap}>{contractId ?? "-"}</td>
    )}
    <td className={Styles.summaryCellWrap}>
      {formatDate(item?.dateStartAt)} - {formatDate(item?.dateEndAt)}
    </td>
    <td className={Styles.summaryCellWrap}>
      {formatSlotTime(item?.slotStartAt)} - {formatSlotTime(item?.slotEndAt)}
    </td>
    <td className={Styles.summaryCell}>{item?.requiredQty}</td>
    <td className={Styles.summaryCell}>{item?.minimumQty}</td>
    {layout === "full" && <td className={Styles.summaryCellWrap}>{bidCell}</td>}
    {layout !== "withdrawn" && (
      <td className={Styles.summaryCell}>
        <button
          onClick={() => onViewBidHistory(item?.id)}
          className={Styles.viewBidHistoryBtn}
        >
          View
        </button>
      </td>
    )}
  </>
);

const FullRows = ({ reqData, contractId, onViewBidHistory }) =>
  reqData.map((item, idx) => (
    <React.Fragment key={idx}>
      <table
        className={Styles.summaryTable}
        style={{ tableLayout: "fixed", width: "100%" }}
      >
        <InnerColGroup layout="full" />
        <tbody>
          <tr>
            <ReqRowCells
              item={item}
              layout="full"
              contractId={contractId}
              bidCell={
                <>
                  {item?.lowestBidQty ?? "-"} / {item?.lowestBidPrice ?? "-"}
                </>
              }
              onViewBidHistory={onViewBidHistory}
            />
          </tr>
        </tbody>
      </table>
    </React.Fragment>
  ));

const ReducedRows = ({ reqData, layout, onViewBidHistory }) =>
  reqData.map((item, idx) => (
    <React.Fragment key={idx}>
      <table
        className={Styles.summaryTable}
        style={{ tableLayout: "fixed", width: "100%" }}
      >
        <InnerColGroup layout={layout} />
        <tbody>
          <tr>
            <ReqRowCells
              item={item}
              layout={layout}
              onViewBidHistory={onViewBidHistory}
            />
          </tr>
        </tbody>
      </table>
    </React.Fragment>
  ));

const SummaryRow = ({
  ele,
  index,
  expanded,
  onToggle,
  reqData,
  reqLoading,
  status,
}) => {
  const isExpired = status?.toLowerCase() === "expired";
  const isExpanded = expanded === index;
  const layout = getInnerLayout(status);
  const showEntityName = layout === "full";

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [bidHistoryModal, setBidHistoryModal] = useState(false);
  const [selectedRequisitionId, setSelectedRequisitionId] = useState(null);
  const { handlePreviewGeneralTerms, PdfPreviewModal } = usePdfPreview();

  const mainScreenMatchData = useSelector(
    (state) => state?.CapacitySlice?.mainScreenActiveConnection,
  );
  const connection = isExpanded ? mainScreenMatchData?.[0] : null;
  const contractId = connection?.contractUniqueId;

  const userId = useSelector(
    (state) => state?.persistedReducer?.user?.user?.userId,
  );
  const isInitiator = Number(ele?.createdBy) === userId;

  const openBidHistory = (requisitionId) => {
    setSelectedRequisitionId(requisitionId);
    setBidHistoryModal(true);
  };

  const handleDownloadTenderRes = async () => {
    try {
      const response = await getDocumentTenderRes(ele?.id);
      if (!response?.data) return;
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `tender_${ele?.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Error downloading tender result:", e);
    }
  };

  if (!ele) return null;
  return (
    <>
      <RFPBidHistory
        open={bidHistoryModal}
        handleClose={() => {
          setBidHistoryModal(false);
          setSelectedRequisitionId(null);
        }}
        tenderId={ele?.id}
        requisitionId={selectedRequisitionId}
        showEntityName={showEntityName}
      />
      <PdfPreviewModal />
      <div className={isExpanded ? "" : Styles.detailsMainDrop_inactive}>
        <Accordion
          style={{
            backgroundColor: "#f3f5f8",
            marginTop: index === 0 ? "0px" : "3px",
            border: isExpanded ? "1px solid var(--primary-color-text)" : "none",
            borderRadius: "10px",
          }}
          expanded={isExpanded}
          onChange={() => onToggle(index)}
        >
          <AccordionSummary
            style={{ backgroundColor: "white", borderRadius: "8px" }}
          >
            <div className={Styles.accordionSummaryContainer}>
              <table
                className={Styles.summaryTable}
                style={{ tableLayout: "fixed", width: "100%" }}
              >
                <colgroup>
                  <col style={{ width: "5%" }} />
                  <col style={{ width: isExpired ? "30%" : "35%" }} />
                  <col style={{ width: isExpired ? "35%" : "60%" }} />
                  {isExpired && <col style={{ width: "30%" }} />}
                </colgroup>
                <tbody>
                  <tr>
                    <td className={Styles.summaryCell}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 6,
                        }}
                      >
                        <ExpandMoreIcon
                          style={{ fontSize: "18px" }}
                          className={`${Styles.expandIconLeft} ${
                            isExpanded ? Styles.rotateIcon : ""
                          }`}
                        />
                        {index + 1}
                      </div>
                    </td>
                    <td className={Styles.summaryCell}>{ele?.tenderNumber}</td>
                    <td className={Styles.summaryCell}>
                      {formatCustomDate(ele?.tenderStartDateTime)} -{" "}
                      {formatCustomDate(ele?.tenderCloseDateTime)}
                    </td>
                    {isExpired && (
                      <td className={Styles.summaryCell}>
                        {formatExpireDate(ele?.expireAt)}
                      </td>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </AccordionSummary>

          <AccordionDetails
            style={{ backgroundColor: "white", borderRadius: "8px" }}
          >
            <div
              style={{
                border: "1px solid var(--primary-color-text)",
                margin: "0px 10px 10px 10px",
              }}
              className={Styles.accordionDetailsContainerUploadDocs}
            >
              {reqLoading ? (
                <div className={Styles.loaderContainer}>
                  <div
                    className={Styles.loader}
                    role="status"
                    aria-label="loading connection"
                  />
                </div>
              ) : Array.isArray(reqData) && reqData.length ? (
                <>
                  <SummaryInnerHeaderRow layout={layout} />
                  <div style={{ padding: "10px" }}>
                    <div
                      style={{
                        border: "1px solid #DFEAF2",
                        padding: "5px",
                        borderRadius: "8px",
                      }}
                    >
                      {layout === "full" ? (
                        <FullRows
                          reqData={reqData}
                          contractId={contractId}
                          onViewBidHistory={openBidHistory}
                        />
                      ) : (
                        <ReducedRows
                          reqData={reqData}
                          layout={layout}
                          onViewBidHistory={openBidHistory}
                        />
                      )}
                      <div
                        className={Styles.statusBtnContainer}
                        style={{ paddingRight: "15px" }}
                      >
                        <div className={Styles.leftSideBtnContainer}>
                          <button
                            title={
                              ele?.tenderDoc
                                ? "View General Terms & Conditions"
                                : "Specific T&C not available"
                            }
                            onClick={() =>
                              handlePreviewGeneralTerms(ele?.tenderDoc)
                            }
                            disabled={!ele?.tenderDoc}
                            style={{
                              cursor: ele?.tenderDoc
                                ? "pointer"
                                : "not-allowed",
                              opacity: ele?.tenderDoc ? 1 : 0.6,
                            }}
                            className={Styles.leftSideBtn}
                          >
                            Specific T&C
                            <CallMadeIcon
                              style={{
                                fontSize: "8px",
                                position: "absolute",
                                top: "0px",
                              }}
                            />
                          </button>
                          <button
                            onClick={() =>
                              handlePreviewGeneralTerms(systemTnCUrl, {
                                hideHeader: true,
                              })
                            }
                            className={Styles.leftSideBtn}
                          >
                            T&C
                            <CallMadeOutlinedIcon
                              style={{
                                fontSize: "10px",
                                position: "absolute",
                                top: "-2px",
                              }}
                            />
                          </button>
                        </div>
                        {ele?.tenderStatus === "TERMS" && (
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              onClick={() => {
                                let capId;
                                if (isInitiator) {
                                  capId = ele?.capacityId;
                                } else {
                                  capId =
                                    ele?.generatorCapacityId ||
                                    ele?.consumerCapacityId;
                                }
                                localStorage.setItem("activetenderId", ele?.id);
                                sessionStorage.setItem(
                                  EXPANDED_TENDER_KEY,
                                  String(ele?.id),
                                );
                                dispatch(activeTenderId(ele?.id));
                                navigate(
                                  `/tender_active?id=${capId}&source=archive`,
                                );
                              }}
                              className={Styles.rightSideBtn}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              View Details
                              <CallMadeIcon
                                style={{
                                  fontSize: "10px",
                                  paddingBottom: "15px",
                                }}
                              />
                            </button>
                            <button
                              onClick={handleDownloadTenderRes}
                              className={Styles.rightSideBtn}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <SaveAltIcon style={{ fontSize: "14px" }} />
                              Result
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className={Styles.noDataContainer}>
                  No matching records
                </div>
              )}
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    </>
  );
};

// Initiate tab of the TradeConnect archive: for generator, lists tenders via
// the dedicated generator-creator archive endpoint. For consumer, this tab
// swaps to the getRaArchive(role=consumer) source (the same source the
// "Place Bid" tab uses for generator), since consumer's "Place Bid" tab now
// uses the consumer-bidder endpoint instead — see RFQ.jsx's
// useConsumerBidderSource.
const TradeConnectInitiateArchive = ({ type, status, auctionType }) => {
  const dispatch = useDispatch();
  const [reqData, setReqData] = useState([]);
  const [reqLoading, setReqLoading] = useState(false);
  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  )?.toLowerCase();
  const [expanded, setExpanded] = useState(0);
  const [page, setPage] = useState(0);

  const isConsumer = role && role !== "generator";

  const [consumerData, setConsumerData] = useState([]);
  const [consumerLoading, setConsumerLoading] = useState(false);
  const [consumerHasMore, setConsumerHasMore] = useState(true);

  const generatorData = useSelector(
    (state) => state?.tokenAccessArchive?.generatorCreatorArchive,
  );
  const generatorTotalPages = useSelector(
    (state) => state?.tokenAccessArchive?.generatorCreatorArchiveTotalPages,
  );
  const generatorLoading = useSelector(
    (state) => state?.tokenAccessArchive?.loading,
  );

  const data = isConsumer ? consumerData : generatorData;
  const loading = isConsumer ? consumerLoading : generatorLoading;
  const hasMore = isConsumer
    ? consumerHasMore
    : page < (generatorTotalPages ?? 0) - 1;
  const handleLoadMore = () => setPage((prev) => prev + 1);

  useEffect(() => {
    setExpanded(0);
    setPage(0);
  }, [type, status, auctionType, role]);

  const handleAccordionChange = useCallback((val) => {
    setExpanded((prev) => (prev === val ? -1 : Number(val)));
  }, []);

  useEffect(() => {
    if (!role) return;
    if (role === "generator") {
      dispatch(
        generatorCreatorArchiveGet({
          status,
          page,
          size: 10,
          ...(auctionType && { auctionType }),
        }),
      );
      return;
    }

    let mounted = true;
    async function fetchConsumerData() {
      try {
        setConsumerLoading(true);
        const res = await getRaArchive({
          role,
          status,
          page,
          size: 10,
          tenderType: "CB",
          ...(auctionType && { auctionType }),
        });
        if (!mounted) return;
        if (res?.data?.code === 200) {
          const newData = res.data.data.page || [];
          setConsumerData((prev) =>
            page === 0 ? newData : [...prev, ...newData],
          );
          const { totalPages, currentPage } = res.data.data;
          setConsumerHasMore(currentPage < totalPages - 1);
        } else {
          setConsumerData([]);
          setConsumerHasMore(false);
        }
      } catch (error) {
        if (mounted) {
          console.error("Error fetching TradeConnect initiate archive:", error);
          setConsumerData([]);
          setConsumerHasMore(false);
        }
      } finally {
        if (mounted) setConsumerLoading(false);
      }
    }
    fetchConsumerData();
    return () => {
      mounted = false;
    };
  }, [dispatch, role, status, page, auctionType]);

  const fetchReqData = useCallback(async ({ id, bidStage }) => {
    try {
      setReqLoading(true);
      const res = await getequisitionsList(id, bidStage);
      if (res?.data?.code === 200) setReqData(res.data.data);
    } catch (error) {
      console.error("Error fetching requisition data:", error);
    } finally {
      setReqLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!Array.isArray(data) || expanded === -1) return;
    const item = data[expanded];
    if (!item?.id) return;

    fetchReqData({ id: item.id, bidStage: "CB" });

    // Initiate tab: the current user's entity is always the tender creator,
    // so the connection lookup always uses the tender's own capacityId,
    // regardless of which archive-list endpoint fed this row.
    const capId = item?.capacityId;
    if (capId) {
      dispatch(fetchMainScreenActiveConnection({ capId }));
      getMainScreenConnectionDetails(capId).catch(() => {});
    }
  }, [data, dispatch, expanded, fetchReqData, status]);

  return (
    <div
      style={{
        height: "calc(100vh - 270px)",
        overflowY: "auto",
        overflowX: "hidden",
        paddingRight: "4px",
      }}
    >
      <LoaderNoData loading={loading} dataLength={data?.length ?? 0} />
      {data?.length > 0 && <SummaryHeader status={status} />}
      <LoadMorePagination
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        loading={loading}
      >
        {data?.map((ele, index) => (
          <SummaryRow
            key={ele?.id ?? ele?.name ?? index}
            ele={ele}
            reqLoading={reqLoading}
            index={index}
            reqData={reqData}
            expanded={expanded}
            onToggle={handleAccordionChange}
            status={status}
          />
        ))}
      </LoadMorePagination>
    </div>
  );
};

export default TradeConnectInitiateArchive;
